import {
  separarTiposPorVolume,
  seriesDeTipos,
  linhasPorPeriodo,
  hintDeTipos,
  MAX_SERIES_COM_COR,
  ROTULO_OUTROS
} from "./seriesPorTipo";

/* Ajuda a montar o formato que o backend devolve em `por_tipo`. */
const reg = (periodo, tipo, concluidas) => ({
  periodo,
  tipo_atividade: tipo,
  concluidas
});

/* O formatador real (formatPeriodo) depende de locale; aqui a identidade basta —
   o que estes testes verificam é o agrupamento, não a formatação de data. */
const idPeriodo = p => p;

describe("separarTiposPorVolume", () => {
  it("com até quatro tipos, todos ficam com cor própria e não há Outros", () => {
    const dados = [
      reg("2026-01", "Questionário", 5),
      reg("2026-01", "Redação", 4),
      reg("2026-01", "Simulado", 3),
      reg("2026-01", "Atividade", 2)
    ];
    const { comCorPropria, agrupados, tipos } = separarTiposPorVolume(dados);

    expect(comCorPropria).toHaveLength(4);
    expect(agrupados).toEqual([]);
    expect(tipos).not.toContain(ROTULO_OUTROS);
  });

  it("ordena por volume total, somando todos os períodos do tipo", () => {
    /* "Redação" perde em cada período isolado, mas ganha no total — é o total
       que decide quem merece cor própria. */
    const dados = [
      reg("2026-01", "Simulado", 10),
      reg("2026-01", "Redação", 9),
      reg("2026-02", "Redação", 9)
    ];
    const { comCorPropria } = separarTiposPorVolume(dados);

    expect(comCorPropria[0]).toBe("Redação");
    expect(comCorPropria[1]).toBe("Simulado");
  });

  it("acima de quatro tipos, os de menor volume vão para Outros", () => {
    const dados = [
      reg("2026-01", "A", 100),
      reg("2026-01", "B", 90),
      reg("2026-01", "C", 80),
      reg("2026-01", "D", 70),
      reg("2026-01", "E", 5),
      reg("2026-01", "F", 1)
    ];
    const { comCorPropria, agrupados, tipos } = separarTiposPorVolume(dados);

    expect(comCorPropria).toEqual(["A", "B", "C", "D"]);
    expect(agrupados).toEqual(["E", "F"]);
    expect(tipos[tipos.length - 1]).toBe(ROTULO_OUTROS);
  });

  it("trata concluidas ausente como zero em vez de gerar NaN", () => {
    const dados = [reg("2026-01", "A", undefined), reg("2026-01", "B", 3)];
    const { comCorPropria } = separarTiposPorVolume(dados);

    expect(comCorPropria).toEqual(["B", "A"]);
  });
});

describe("seriesDeTipos", () => {
  /* Este é o teste que o defeito antigo não passaria. Antes a cor saía de
     `var(--color-chart-${(i % 8) + 1})`: com nove tipos, o 9º recebia
     --color-chart-1, a mesma cor do 1º, e duas séries diferentes apareciam
     como se fossem a mesma. */
  it("nunca repete cor entre séries", () => {
    const dados = Array.from({ length: 12 }, (_, i) =>
      reg("2026-01", `Tipo ${i}`, 100 - i)
    );
    const { tipos } = separarTiposPorVolume(dados);
    const cores = seriesDeTipos(tipos).map(s => s.color);

    expect(new Set(cores).size).toBe(cores.length);
  });

  it("não usa mais slots de cor do que a paleta tem validados", () => {
    const dados = Array.from({ length: 12 }, (_, i) =>
      reg("2026-01", `Tipo ${i}`, 100 - i)
    );
    const { tipos } = separarTiposPorVolume(dados);
    const series = seriesDeTipos(tipos);

    /* Quatro com cor da paleta + Outros em cinza neutro. */
    expect(series).toHaveLength(MAX_SERIES_COM_COR + 1);
    series.slice(0, MAX_SERIES_COM_COR).forEach((s, i) => {
      expect(s.color).toBe(`var(--color-chart-${i + 1})`);
    });
    expect(series[MAX_SERIES_COM_COR].color).toBe("var(--color-ink-faint)");
  });
});

describe("linhasPorPeriodo", () => {
  it("uma linha por período, com cada tipo em sua coluna", () => {
    const dados = [
      reg("2026-01", "A", 3),
      reg("2026-01", "B", 2),
      reg("2026-02", "A", 7)
    ];
    const { tipos, agrupados } = separarTiposPorVolume(dados);
    const linhas = linhasPorPeriodo(dados, tipos, agrupados, idPeriodo);

    expect(linhas).toHaveLength(2);
    expect(linhas[0]).toMatchObject({ periodo_fmt: "2026-01", A: 3, B: 2 });
    /* Período sem registro do tipo vira zero, não undefined — senão o Recharts
       corta a linha no meio em vez de mostrar o vale. */
    expect(linhas[1]).toMatchObject({ periodo_fmt: "2026-02", A: 7, B: 0 });
  });

  it("SOMA os tipos agrupados na coluna Outros, sem perder nenhum", () => {
    const dados = [
      reg("2026-01", "A", 100),
      reg("2026-01", "B", 90),
      reg("2026-01", "C", 80),
      reg("2026-01", "D", 70),
      reg("2026-01", "E", 5),
      reg("2026-01", "F", 4),
      reg("2026-01", "G", 3)
    ];
    const { tipos, agrupados } = separarTiposPorVolume(dados);
    const [linha] = linhasPorPeriodo(dados, tipos, agrupados, idPeriodo);

    expect(linha[ROTULO_OUTROS]).toBe(12);
  });

  it("ordena os períodos em ordem crescente", () => {
    const dados = [
      reg("2026-03", "A", 1),
      reg("2026-01", "A", 1),
      reg("2026-02", "A", 1)
    ];
    const { tipos, agrupados } = separarTiposPorVolume(dados);
    const linhas = linhasPorPeriodo(dados, tipos, agrupados, idPeriodo);

    expect(linhas.map(l => l.periodo_fmt)).toEqual([
      "2026-01",
      "2026-02",
      "2026-03"
    ]);
  });
});

describe("hintDeTipos", () => {
  it("sem agrupamento, não menciona Outros", () => {
    expect(hintDeTipos([])).toBe("Concluídas por tipo de atividade");
  });

  /* O agrupamento nunca é silencioso: quem lê o gráfico precisa saber que há
     tipos somados ali, senão "Outros" parece uma categoria do próprio dado. */
  it("com agrupamento, diz quantos tipos entraram em Outros", () => {
    expect(hintDeTipos(["E"])).toContain("1 tipo de menor volume");
    expect(hintDeTipos(["E", "F"])).toContain("2 tipos de menor volume");
  });
});
